import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { useDispatch, useSelector } from 'react-redux';

import {
  getRenderPatientSuggestionFunction,
  getSuggestionValue,
  renderInputComponent,
} from 'FRONTEND/components/Calendar/AppointmentForm/stepperHelper';
import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import { pickPatient } from 'FRONTEND/reducers/bookingSlice';
import {
  clearSearch as clearPatientSearch,
  expectCreation,
  searchPatients,
  selectAllMatchingPatientsByTenant,
  selectPatientById,
} from 'FRONTEND/reducers/patientSlice';
import { MAX_SEARCH_RESULT_NUMBER, PATIENT_MINIMUM_SEARCH_CHARACTERS, FormTypes } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { PATIENT_CREATE_PATH } from 'FRONTEND/utils/routes';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

const PREMIOCARE_PATIENT_MANDATORY_FIELDS = [
  { key: 'firstname', labelKey: 'First name' },
  { key: 'lastname', labelKey: 'Last name' },
  { key: 'email', labelKey: 'Email' },
  { key: 'gender', labelKey: 'Gender' },
  { key: 'birthdate', labelKey: 'Date of birth' },
  { key: 'birthplaceInsee', labelKey: 'Birthplace (INSEE code)' },
];
const isFieldPresent = (value) => Boolean(value) && value !== 'None';

export const PatientStep = ({
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const isSmall = useSelector((state) => state.app.isSmall);
  const isLoading = useSelector((state) => state.app.requestCounter[searchPatients.pending.toString()] > 0);
  const selectedLocation = useSelector((state) => state.booking.selectedLocationId ? state.location.entities[state.booking.selectedLocationId] : null);
  const selectedPatient = useSelector((state) => selectPatientById(state, state.booking.selectedPatientId));
  const patientList = useSelector((state) => selectAllMatchingPatientsByTenant(state, selectedLocation?.tenantId));
  const [patientQuery, setPatientQuery] = useState('');

  const formType = useSelector((state) => state.booking.formType);
  const tenantList = useSelector((state) => state.user.tenantList);
  const isLocationFormPremiocareTenant = formType === FormTypes.LOCATION_FORM &&
    Boolean(selectedLocation?.tenantId && tenantList[selectedLocation.tenantId]?.premiocare_cabinet_id);

  const missingPatientFields = isLocationFormPremiocareTenant && selectedPatient ? PREMIOCARE_PATIENT_MANDATORY_FIELDS.filter(({ key }) => !isFieldPresent(selectedPatient[key])) : [];

  useEffect(() => {
    if (shouldReset) {
      setPatientQuery('');
      dispatch(pickPatient({ id: null }));
    }
  }, [shouldReset]);

  const handlePatientChange = (id) => {
    dispatch(pickPatient({ id }));
    onStepCompleted();
  };

  const handlePatientCreateClick = () => {
    dispatch(expectCreation());
    $.pjax({ url: PATIENT_CREATE_PATH, container: PJAX_MAIN_CONTAINER_SELECTOR });
  };

  const patientSuggestProps = {
    renderInputComponent,
    suggestions: patientList.slice(0, MAX_SEARCH_RESULT_NUMBER),
    onSuggestionsFetchRequested: ({ value }) => value.length >= PATIENT_MINIMUM_SEARCH_CHARACTERS && dispatch(searchPatients({ name: value })),
    onSuggestionsClearRequested: () => dispatch(clearPatientSearch()),
    getSuggestionValue,
    renderSuggestion: getRenderPatientSuggestionFunction(handlePatientChange),
    onSuggestionSelected: (event, { suggestion, method }) => method === 'enter' ? handlePatientChange(suggestion.id) : null,
  };

  let displayName = `${selectedPatient?.firstname ? `${selectedPatient?.firstname} ` : ''}${selectedPatient?.lastname ? `${selectedPatient?.lastname} ` : ''}`;
  displayName = displayName || i18n._('Anonymous');

  const warningText = `${i18n._('This patient is missing mandatory fields for Premiocare appointments:')} ${missingPatientFields.map(({ labelKey }) => i18n._(labelKey)).join(', ')}.`;

  return (
    <StepTemplate
      label={i18n._('Select a patient')}
      isLocked={isLocked}
      onEditClick={() => onStepClick()}
      isStepCompleted={selectedPatient && isCompleted}
      editText={displayName}
      alerts={[
        { condition: missingPatientFields.length > 0, text: warningText, severity: 'warning' },
      ]}
      {...otherProps}
    >
      <Stack direction={isSmall ? 'column' : 'row'} spacing={2} sx={{ width: '100%' }}>
        <div className='contactInput'>
          <Autosuggest
            className='contactInput'
            {...patientSuggestProps}
            inputProps={{
              autoFocus: true,
              placeholder: i18n._('Enter the patient’s name (minimum 3 characters)'),
              value: patientQuery,
              onChange: (event, { newValue }) => setPatientQuery(newValue),
            }}
            renderSuggestionsContainer={({ children, containerProps }) => (
              isLoading ? (
                <Typography color='primary.main'>{i18n._('Searching...')}</Typography>
              ) : patientList.length > 0 ? (
                <Paper {...containerProps} square>
                  {children}
                </Paper>
              ) : patientQuery && (patientQuery.length >= 3 ? <Typography>{i18n._('No patient matches your search.')}</Typography> : <Typography>{i18n._('Type at least three letters to search')}.</Typography>)
            )}
          />
        </div>
        <Stack direction='row' sx={{ marginTop: 2, alignItems: 'center' }}>
          <Typography sx={{ mr: 1 }}>{i18n._('or')}</Typography>
          <Button onClick={handlePatientCreateClick} variant='contained' color='inherit' startIcon={<PersonAddIcon />}>
            {i18n._('Create a patient')}
          </Button>
        </Stack>
      </Stack>
    </StepTemplate>
  );
};
